import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui";
import { Loader } from "@/components/shared";
import { GridPostList, PostStats } from "@/components/shared";
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";

import {
  useGetPostById,
  useGetUserPosts,
  useDeletePost,
  useGetUserById,
} from "@/lib/react-query/queries";
import { multiFormatDateString } from "@/lib/utils";
import { useUserContext } from "@/context/AuthContext";
import { isValidMediaUrl } from "@/lib/appwrite/config";
import SellerRatingBadge from "@/components/shared/SellerRatingBadge";
import CreatorAvatar from "@/components/shared/CreatorAvatar";
import SEOComponent from "@/components/shared/SEOComponent";

const PostDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useUserContext();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);

  const { data: post, isLoading, isError } = useGetPostById(id);
  
  // ✅ Fix: Only call useGetUserById when we have a creatorId
  const { data: creator, isLoading: isCreatorLoading } = useGetUserById(post?.creatorId || "");
  const routeCreator = (location.state as any)?.creatorName;
  const routeCreatorImageUrl = (location.state as any)?.creatorImageUrl;
  const creatorName =
    routeCreator ||
    creator?.name ||
    (post as any)?.creatorName ||
    (post as any)?.userName ||
    (post as any)?.authorName ||
    (post as any)?.creator?.name ||
    "Busiconn member";
  const creatorImageUrl =
    isValidMediaUrl(routeCreatorImageUrl) ? routeCreatorImageUrl :
    isValidMediaUrl(creator?.imageUrl) ? creator.imageUrl :
    isValidMediaUrl((post as any)?.creatorImageUrl) ? (post as any).creatorImageUrl.trim() :
    isValidMediaUrl((post as any)?.creator?.imageUrl) ? (post as any).creator.imageUrl :
    "/assets/icons/profile-placeholder.svg";
  
  const { data: userPosts, isLoading: isUserPostLoading } = useGetUserPosts(
    post?.creatorId
  );
  
  const { mutate: deletePost } = useDeletePost();
  const currentPostTags = post?.tags || [];
 const relatedPosts = userPosts?.documents
  .filter((userPost) => userPost.$id !== id)
  .map((userPost) => {
    // Calculate tag similarity score
    const postTags = userPost.tags || [];
    const commonTags = currentPostTags.filter((tag: any) => 
      postTags.some((postTag: any) => 
        postTag.toLowerCase().includes(tag.toLowerCase()) ||
        tag.toLowerCase().includes(postTag.toLowerCase())
      )
    );
    
    return {
      ...userPost,
      tagScore: commonTags.length,
      commonTags: commonTags
    };
  })
  .sort((a, b) => {
    // Sort by tag relevance first, then by date
    if (b.tagScore !== a.tagScore) {
      return b.tagScore - a.tagScore;
    }
    // If same tag score, sort by most recent
    return new Date(b.$createdAt).getTime() - new Date(a.$createdAt).getTime();
  })
  .slice(0, 6);

  const handleDeletePost = () => {
    deletePost({ postId: id, imageId: post?.imageId });
    navigate(-1);
  };

  const postMedia = Array.isArray(post?.media)
    ? post.media
        .filter((item: any) => isValidMediaUrl(item?.url))
        .sort((left: any, right: any) => (left?.sortOrder ?? 0) - (right?.sortOrder ?? 0))
    : [];
  const mediaFiles = postMedia.length > 0
    ? postMedia.map((item: any) => item.url)
    : post && (typeof post.imageUrl === 'string'
        ? (isValidMediaUrl(post.imageUrl) ? [post.imageUrl.trim()] : [])
        : Array.isArray(post.imageUrl) ? post.imageUrl.filter(isValidMediaUrl).map((url) => url.trim()) : []);
  const mediaTypes = postMedia.length > 0
    ? postMedia.map((item: any) => item.mediaType)
    : Array.isArray(post?.mediaType) ? post.mediaType : [];
  const hasMedia = !!mediaFiles?.length;

  const nextImage = () => {
    if (!mediaFiles || mediaFiles.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % mediaFiles.length);
  };

  const prevImage = () => {
    if (!mediaFiles || mediaFiles.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + mediaFiles.length) % mediaFiles.length);
  };

  const goToSlide = (index: number) => {
    setCurrentImageIndex(index);
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(err => console.log("Play failed:", err));
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Reset carousel when post changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [post?.$id]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(err => console.log("Autoplay prevented:", err));
    }
  }, [post, currentImageIndex]);

  const isVideo = (url: string, index = 0) => {
    return mediaTypes[index]?.toLowerCase?.().startsWith('video/') ||
           mediaTypes[index]?.toLowerCase?.() === 'video' ||
           url?.includes('&type=video') || url?.includes('/view?') || 
           url?.match(/\.(mp4|webm|ogg|mov)$/i);
  };

  // ✅ Show loading state while post or creator is loading
  if (isLoading || !post || isCreatorLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <SEOComponent metadataOnly noIndex={isError || (!isLoading && !post)} />
        <Loader />
      </div>
    );
  }

  return (
    <div className="post_details-container max-w-6xl mx-auto px-4 py-4">
      <SEOComponent
        metadataOnly
        title={`${String(post.caption || 'Product or service').replace(/\s+/g, ' ').slice(0, 70)} | Busiconn`}
        description={String(post.caption || `View this offer from ${creatorName} on Busiconn.`).replace(/\s+/g, ' ').slice(0, 160)}
        image={mediaFiles?.find((url, index) => !isVideo(url, index))}
      />
      {/* Back button */}
      <div className="hidden md:flex w-full mb-6">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="shad-button_ghost">
          <img
            src={"/assets/icons/back.svg"}
            alt="back"
            width={24}
            height={24}
            className="mr-2"
          />
          <p className="small-medium lg:base-medium">Back</p>
        </Button>
      </div>

      <div className={`grid grid-cols-1 gap-6 lg:gap-8 w-full ${hasMedia ? "lg:grid-cols-2" : "max-w-3xl mx-auto"}`}>
        {/* Media Container - Enhanced Carousel */}
        {hasMedia && (
          <div className="w-full">
            <div className="relative w-full">
              {/* Main Media Display */}
              <div className="relative w-full aspect-square bg-black rounded-xl overflow-hidden shadow-2xl">
                {(() => {
                  const currentMedia = mediaFiles[currentImageIndex];
                  
                  return isVideo(currentMedia, currentImageIndex) ? (
                    <div className="relative w-full h-full">
                      <video
                        ref={videoRef}
                        src={currentMedia}
                        playsInline
                        loop
                        muted={isMuted}
                        preload="metadata"
                        disablePictureInPicture
                        controlsList="nodownload nofullscreen noremoteplaybook"
                        className="w-full h-full object-cover"
                        poster="/assets/icons/video-placeholder.png"
                        onClick={togglePlay}
                        onError={(e) => console.error("Video error:", e)}
                      />
                      {/* Video Controls */}
                      <div className="absolute bottom-4 right-4 flex gap-2">
                        <button
                          onClick={toggleMute}
                          className="bg-black/60 backdrop-blur-sm p-3 rounded-full hover:bg-black/80 transition-all duration-200"
                        >
                          {isMuted ? (
                            <VolumeX color="white" size={20} />
                          ) : (
                            <Volume2 color="white" size={20} />
                          )}
                        </button>
                      </div>
                      {/* Play/Pause Overlay */}
                      {!isPlaying && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="bg-white/90 rounded-full p-4">
                            <div className="w-0 h-0 border-l-[20px] border-l-black border-y-[15px] border-y-transparent ml-1"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <img
                      src={currentMedia || "/assets/icons/profile-placeholder.svg"}
                      alt={`Post media ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  );
                })()}

                {/* Navigation Arrows - Only show if multiple media */}
                {mediaFiles.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm p-3 rounded-full hover:bg-black/70 transition-all duration-200 hover:scale-110"
                      aria-label="Previous image"
                    >
                      <ChevronLeft color="white" size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm p-3 rounded-full hover:bg-black/70 transition-all duration-200 hover:scale-110"
                      aria-label="Next image"
                    >
                      <ChevronRight color="white" size={24} />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {mediaFiles.length > 1 && (
                  <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-white text-sm font-medium">
                      {currentImageIndex + 1} / {mediaFiles.length}
                    </span>
                  </div>
                )}
              </div>

              {/* Thumbnail Dots - Only show if multiple media */}
              {mediaFiles.length > 1 && (
                <div className="flex justify-center mt-4 gap-2">
                  {mediaFiles.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-200 ${
                        index === currentImageIndex
                          ? 'bg-primary-500 scale-125'
                          : 'bg-gray-400 hover:bg-gray-300'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Thumbnail Strip for larger screens */}
              {mediaFiles.length > 1 && (
                <div className="hidden md:flex mt-4 gap-2 overflow-x-auto pb-2">
                  {mediaFiles.map((media, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        index === currentImageIndex
                          ? 'border-primary-500 ring-2 ring-primary-500/30'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {isVideo(media, index) ? (
                        <div className="w-full h-full bg-black flex items-center justify-center">
                          <Volume2 color="white" size={16} />
                        </div>
                      ) : (
                        <img
                          src={media}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Post Details Container */}
        <div className="post_details-info flex flex-col">
          {/* Creator Info */}
          <div className="flex flex-wrap items-start justify-between gap-3 w-full mb-6 sm:flex-nowrap sm:items-center sm:gap-0">
            <Link
              to={`/profile/${post?.creatorId}`}
              className="flex min-w-0 items-center gap-3 group">
              <CreatorAvatar
                src={creatorImageUrl}
                name={creatorName}
                className="w-12 h-12 lg:w-14 lg:h-14 shrink-0 rounded-full object-cover ring-2 ring-gray-200 group-hover:ring-primary-500 transition-all duration-200"
              />
              <div className="flex min-w-0 gap-1 flex-col">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="break-words base-medium lg:body-bold text-light-1 group-hover:text-primary-500 transition-colors">
                    {creatorName}
                  </p>
                  {post?.isSellingPost && <SellerRatingBadge creatorId={post?.creatorId} />}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-light-3 sm:flex-nowrap">
                  <p className="subtle-semibold lg:small-regular">
                    {multiFormatDateString(post?.$createdAt)}
                  </p>
                  {post?.location && (
                    <>
                      <span>•</span>
                      <p className="subtle-semibold lg:small-regular">
                        {post?.location}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </Link>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Link
                to={`/update-post/${post?.$id}`}
                className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${
                  user.id !== post?.creatorId && "hidden"
                }`}>
                <img
                  src={"/assets/icons/edit.svg"}
                  alt="edit"
                  width={20}
                  height={20}
                />
              </Link>

              <Button
                onClick={handleDeletePost}
                variant="ghost"
                size="sm"
                className={`p-2 hover:bg-rose-50 hover:text-rose-600 ${
                  user.id !== post?.creatorId && "hidden"
                }`}>
                <img
                  src={"/assets/icons/delete.svg"}
                  alt="delete"
                  width={20}
                  height={20}
                />
              </Button>
            </div>
          </div>

          <hr className="border border-dark-4/20 mb-6" />

          {/* Post Content */}
          <div className="flex flex-col flex-1 w-full">
            {/* Caption */}
            {post?.caption && (
              <div className="mb-4">
                <p className="text-light-1 leading-relaxed whitespace-pre-wrap">
                  {post.caption}
                </p>
              </div>
            )}

            {/* Tags */}
            {post?.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map((tag: string, index: number) => (
                  tag.trim() && (
                    <span
                      key={`${tag}-${index}`}
                      className="bg-primary-500/10 text-primary-500 px-3 py-1 rounded-full text-sm font-medium hover:bg-primary-500/20 transition-colors cursor-pointer"
                    >
                      #{tag.trim()}
                    </span>
                  )
                ))}
              </div>
            )}

            {/* Training Availability Status */}
            {post.individual === 'skilled-employee' && (
              <div className="mb-4">
                {post.isTrainingAvailable ? (
                  <div className="bg-green-50 border border-green-200 px-4 py-3 rounded-lg inline-flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-700 font-semibold">Available to Train</span>
                  </div>
                ) : (
                  <div className="bg-rose-50 border border-rose-200 px-4 py-3 rounded-lg inline-flex items-center gap-2">
                    <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
                    <span className="text-rose-700 font-semibold">Unavailable to Train</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Post Stats */}
          <div className="w-full mt-4">
            <PostStats post={post} userId={user.id} postCreatorId={post.creatorId}/>
          </div>
        </div>
      </div>

      {/* Related Posts Section */}
      <div className="w-full mt-12">
        <hr className="border border-dark-4/20 mb-8" />
        <h3 className="body-bold md:h3-bold w-full mb-6">
          More Related Posts
        </h3>
        {isUserPostLoading || !relatedPosts ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : relatedPosts.length > 0 ? (
          <GridPostList posts={relatedPosts} />
        ) : (
          <div className="text-center py-8 text-light-3">
            <p>No related posts found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDetails;
